import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfDay, subMonths, eachDayOfInterval, endOfDay } from "date-fns";
import { getAllMetrics } from "@/lib/metricDefinitions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Search, Trophy } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface CompareHeadToHeadProps {
  currentUserId: string;
  preselectedOpponentId?: string;
}

interface DailyEntry {
  entry_date: string;
  [key: string]: any;
}

interface CustomMetric {
  metric_key: string;
  metric_name: string;
  metric_emoji: string;
  metric_unit: string;
  metric_type?: "numeric" | "boolean";
}

interface Profile {
  id: string;
  username: string;

  avatar_url: string | null;
  is_public: boolean;
}

const blueColor = "rgb(59 130 246)"; // blue-500 (YOU)
const redColor = "rgb(239 68 68)"; // red-500 (opponent)
const tieColor = "rgb(148 163 184)"; // slate-400
const emptyColor = "rgb(156 163 175)"; // gray-400 (better for dark mode)

const CompareHeadToHead = ({
 currentUserId, preselectedOpponentId }: CompareHeadToHeadProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOpponentId, setSelectedOpponentId] = useState<string>(preselectedOpponentId || "");
  const [userMetric, setUserMetric] = useState<string>("");
  const [opponentMetric, setOpponentMetric] = useState<string>("");
  
  // Calculate date range (3 months back)
  const endDate = endOfDay(new Date());
  const startDate = startOfDay(subMonths(new Date(), 3));

  // Set preselected opponent if provided
  useEffect(() => {
    if (preselectedOpponentId && preselectedOpponentId !== selectedOpponentId) {
      setSelectedOpponentId(preselectedOpponentId);
    }
  }, [preselectedOpponentId]);

  // Helper for avatar URL - handles URLs that may already have cache buster
  const getAvatarUrl = (avatarUrl: string | null | undefined): string | undefined => {
    if (!avatarUrl) return undefined;
    // If URL already has a timestamp, use it as-is
    if (avatarUrl.includes('?t=') || avatarUrl.includes('&t=')) {
      return avatarUrl;
    }
    const separator = avatarUrl.includes('?') ? '&' : '?';
    return `${avatarUrl}${separator}t=${Date.now()}`;
  };

  // Fetch current user profile (avatar/username only)
  // NOTE: Uses unique query key to avoid cache collision with useProfile hook
  const { data: currentUserProfile } = useQuery({
    queryKey: ["profile-compare", currentUserId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, avatar_url")
        .eq("id", currentUserId)
        .single();
      
      if (error) throw error;
      return data as Profile;
    },
    enabled: !!currentUserId,
  });

  // Fetch blocked users
  const { data: blockedUserIds = [] } = useQuery({
    queryKey: ["blocked-users", currentUserId],
    queryFn: async () => {
      const { data: usersIBlocked, error: error1 } = await supabase
        .from("blocked_users")
        .select("blocked_user_id")
        .eq("user_id", currentUserId);
      
      const { data: usersWhoBlockedMe, error: error2 } = await supabase
        .from("blocked_users")
        .select("user_id")
        .eq("blocked_user_id", currentUserId);
      
      if (error1 || error2) throw error1 || error2;
      
      const blockedByMe = usersIBlocked?.map(b => b.blocked_user_id) || [];
      const blockedMe = usersWhoBlockedMe?.map(b => b.user_id) || [];
      return [...new Set([...blockedByMe, ...blockedMe])];
    },
  });

  // Search users
  const { data: searchResults = [], isLoading: searching } = useQuery({
    queryKey: ["user-search-compare", searchQuery, blockedUserIds],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];

      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, avatar_url, is_public")
        .or(`username.ilike.%${searchQuery}%`)
        .not("username", "is", null)
        .neq("id", currentUserId);

      if (error) throw error;

      if (!data || data.length === 0) return [];

      // Filter by blocked and deleted users, private profiles, and follow status
      const userIds = data.map((u: any) => u.id);
      
      // Check active users
      const { data: activeUserIdsArray, error: rpcError } = await (supabase as any).rpc('get_active_user_ids', {
        user_ids: userIds
      });

      if (rpcError) {
        console.error('Error checking active users:', rpcError);
        return data.filter((user: any) => !blockedUserIds.includes(user.id));
      }

      const activeUserIds = new Set((activeUserIdsArray as string[]) || []);

      // Filter by active, not blocked
      let filtered = data.filter(
        (user: any) => !blockedUserIds.includes(user.id) && activeUserIds.has(user.id)
      );

      // For private profiles, check if we're following
      const privateProfiles = filtered.filter((u: any) => !u.is_public);
      
      if (privateProfiles.length > 0) {
        const { data: followsData } = await supabase
          .from("follows")
          .select("following_id")
          .eq("follower_id", currentUserId)
          .in("following_id", privateProfiles.map(p => p.id));
        
        const followingIds = new Set(followsData?.map(f => f.following_id) || []);
        
        // Keep public profiles and private profiles we follow
        filtered = filtered.filter((u: any) => u.is_public || followingIds.has(u.id));
      }

      return filtered as Profile[];
    },
    enabled: searchQuery.length >= 2,
  });

  // Fetch opponent profile
  // Use a distinct query key to avoid overwriting the full profile cache from useProfile
  const { data: opponentProfile } = useQuery({
    queryKey: ["compare-opponent-profile", selectedOpponentId],
    queryFn: async () => {
      if (!selectedOpponentId) return null;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, avatar_url")
        .eq("id", selectedOpponentId)
        .single();
      
      if (error) throw error;
      return data as Profile;
    },
    enabled: !!selectedOpponentId,
  });

  // Fetch all available metrics
  const { data: allMetrics = [] } = useQuery({
    queryKey: ["all-metrics"],
    queryFn: getAllMetrics,
  });

  // Fetch current user's custom metrics
  const { data: userCustomMetrics = [] } = useQuery({
    queryKey: ["custom-metrics", currentUserId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custom_metrics")
        .select("*")
        .eq("user_id", currentUserId)
        .order("metric_name");
      
      if (error) throw error;
      return data as CustomMetric[];
    },
    enabled: !!currentUserId,
  });

  // Fetch opponent's custom metrics
  const { data: opponentCustomMetrics = [] } = useQuery({
    queryKey: ["custom-metrics", selectedOpponentId],
    queryFn: async () => {
      if (!selectedOpponentId) return [];
      
      const { data, error } = await supabase
        .from("custom_metrics")
        .select("*")
        .eq("user_id", selectedOpponentId)
        .order("metric_name");
      
      if (error) throw error;
      return data as CustomMetric[];
    },
    enabled: !!selectedOpponentId,
  });

  // Fetch current user's metric preferences (enabled metrics)
  const { data: userMetricPreferences } = useQuery({
    queryKey: ["metric-preferences", currentUserId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("metric_preferences")
        .select("*")
        .eq("user_id", currentUserId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentUserId,
  });

  // Fetch opponent's metric preferences (enabled metrics)
  const { data: opponentMetricPreferences } = useQuery({
    queryKey: ["metric-preferences", selectedOpponentId],
    queryFn: async () => {
      if (!selectedOpponentId) return null;
      
      const { data, error } = await supabase
        .from("metric_preferences")
        .select("*")
        .eq("user_id", selectedOpponentId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!selectedOpponentId,
  });

  // Get enabled built-in metrics for a user
  const getEnabledBuiltInMetrics = (preferences: any): Set<string> => {
    if (!preferences) return new Set();
    
    const enabledMetrics = [
      ...(preferences.nutrition_metrics || []),
      ...(preferences.activity_sleep_metrics || []),
      ...(preferences.results_metrics || [])
    ];
    
    return new Set(enabledMetrics);
  };

  // Filter metrics to only show enabled/active ones
  const userEnabledBuiltInMetrics = getEnabledBuiltInMetrics(userMetricPreferences);
  const opponentEnabledBuiltInMetrics = getEnabledBuiltInMetrics(opponentMetricPreferences);

  // Combine metrics for each user - only include enabled built-in metrics
  const userAvailableMetrics = [
    ...allMetrics
      .filter(m => userEnabledBuiltInMetrics.has(m.key))
      .map(m => ({ key: m.key, label: `${m.emoji} ${m.label}`, emoji: m.emoji, name: m.label })),
    ...userCustomMetrics.map(m => ({ key: m.metric_key, label: `${m.metric_emoji} ${m.metric_name}`, emoji: m.metric_emoji, name: m.metric_name }))
  ];

  const opponentAvailableMetrics = [
    ...allMetrics
      .filter(m => opponentEnabledBuiltInMetrics.has(m.key))
      .map(m => ({ key: m.key, label: `${m.emoji} ${m.label}`, emoji: m.emoji, name: m.label })),
    ...opponentCustomMetrics.map(m => ({ key: m.metric_key, label: `${m.metric_emoji} ${m.metric_name}`, emoji: m.metric_emoji, name: m.metric_name }))
  ];

  // Fetch user entries
  const { data: userEntries = [] } = useQuery({
    queryKey: ["compare-entries", currentUserId, startDate.toISOString(), endDate.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("daily_entries")
        .select("*")
        .eq("user_id", currentUserId)
        .gte("entry_date", format(startDate, "yyyy-MM-dd"))
        .lte("entry_date", format(endDate, "yyyy-MM-dd"))
        .order("entry_date", { ascending: true });
      
      if (error) throw error;
      return data as DailyEntry[];
    },
    enabled: !!currentUserId,
  });

  // Fetch opponent entries
  const { data: opponentEntries = [] } = useQuery({
    queryKey: ["compare-entries", selectedOpponentId, startDate.toISOString(), endDate.toISOString()],
    queryFn: async () => {
      if (!selectedOpponentId) return [];
      
      const { data, error } = await supabase
        .from("daily_entries")
        .select("*")
        .eq("user_id", selectedOpponentId)
        .gte("entry_date", format(startDate, "yyyy-MM-dd"))
        .lte("entry_date", format(endDate, "yyyy-MM-dd"))
        .order("entry_date", { ascending: true });
      
      if (error) throw error;
      return data as DailyEntry[];
    },
    enabled: !!selectedOpponentId,
  });

  // Fetch user goals
  const { data: userGoals } = useQuery({
    queryKey: ["user-goals", currentUserId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_goals")
        .select("*")
        .eq("user_id", currentUserId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentUserId,
  });

  // Fetch opponent goals
  const { data: opponentGoals } = useQuery({
    queryKey: ["user-goals", selectedOpponentId],
    queryFn: async () => {
      if (!selectedOpponentId) return null;
      
      const { data, error } = await supabase
        .from("user_goals")
        .select("*")
        .eq("user_id", selectedOpponentId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!selectedOpponentId,
  });

  // Create entry lookups
  const userEntryMap = new Map(userEntries.map(e => [e.entry_date, e]));
  const opponentEntryMap = new Map(opponentEntries.map(e => [e.entry_date, e]));
  
  // Generate all days in range
  const allDays = eachDayOfInterval({ start: startDate, end: endDate });
  
  // Organize into weeks
  const weeks: Array<Array<{ date: Date; userEntry: DailyEntry | null; opponentEntry: DailyEntry | null }>> = [];
  let currentWeek: Array<{ date: Date; userEntry: DailyEntry | null; opponentEntry: DailyEntry | null }> = [];
  
  allDays.forEach((date) => {
    const dayOfWeek = date.getDay();
    
    if (dayOfWeek === 0 && currentWeek.length > 0) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
    
    const dateStr = format(date, "yyyy-MM-dd");
    currentWeek.push({ 
      date, 
      userEntry: userEntryMap.get(dateStr) || null,
      opponentEntry: opponentEntryMap.get(dateStr) || null 
    });
  });
  
  if (currentWeek.length > 0) {
    weeks.push(currentWeek);
  }

  // Helper to get value from entry
  const getValueFromEntry = (entry: DailyEntry | null, metricKey: string): number | null => {
    if (!entry) return null;
    const valueKey = metricKey.endsWith('_value') ? metricKey : `${metricKey}`;
    return entry[valueKey] ?? entry[`${metricKey}_value`] ?? null;
  };

  // Get goal value
  const getGoalValue = (metricKey: string, goals: any) => {
    const goalKey = `${metricKey}_goal` as keyof typeof goals;
    const metricInfo = allMetrics.find(m => m.key === metricKey);
    return goals?.[goalKey] as number || metricInfo?.defaultGoal || 100;
  };

  // Calculate percentage of goal
  const getPercentage = (value: number | null, goal: number): number => {
    if (!value) return 0;
    return (value / goal) * 100;
  };

  // Calculate scores
  const calculateScores = () => {
    if (!userMetric || !opponentMetric || !selectedOpponentId) return { userWins: 0, opponentWins: 0, ties: 0 };

    let userWins = 0;
    let opponentWins = 0;
    let ties = 0;

    const userGoal = getGoalValue(userMetric, userGoals);
    const opponentGoal = getGoalValue(opponentMetric, opponentGoals);

    allDays.forEach(date => {
      const dateStr = format(date, "yyyy-MM-dd");
      const userEntry = userEntryMap.get(dateStr);
      const opponentEntry = opponentEntryMap.get(dateStr);

      const userValue = getValueFromEntry(userEntry, userMetric);
      const opponentValue = getValueFromEntry(opponentEntry, opponentMetric);

      if (userValue === null && opponentValue === null) {
        return; // Skip days where both have no data
      }

      const userPercentage = getPercentage(userValue, userGoal);
      const opponentPercentage = getPercentage(opponentValue, opponentGoal);

      if (userPercentage > opponentPercentage) {
        userWins++;
      } else if (opponentPercentage > userPercentage) {
        opponentWins++;
      } else {
        ties++;
      }
    });

    return { userWins, opponentWins, ties };
  };

  const scores = calculateScores();

  // Render comparison graph
  const renderComparisonGraph = () => {
    if (!userMetric || !opponentMetric || !selectedOpponentId) return null;

    const userMetricInfo = userAvailableMetrics.find(m => m.key === userMetric);
    const opponentMetricInfo = opponentAvailableMetrics.find(m => m.key === opponentMetric);
    const userGoal = getGoalValue(userMetric, userGoals);
    const opponentGoal = getGoalValue(opponentMetric, opponentGoals);

    return (
      <div className="space-y-4">
        {/* Header with profiles */}
        <div className="p-4 bg-background rounded-lg border">
          {/* Mobile and Desktop: Unified layout */}
          <div className="space-y-3">
            {/* Profile pictures on same line */}
            <div className="flex items-center justify-center gap-6">
              <Avatar className="w-16 h-16 border-4 border-blue-500">
                <AvatarImage src={getAvatarUrl(currentUserProfile?.avatar_url)} />
                <AvatarFallback>{currentUserProfile?.username?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              
              <div className="text-center px-2">
                <p className="text-2xl font-bold">VS</p>
              </div>
              
              <Avatar className="w-16 h-16 border-4 border-red-500">
                <AvatarImage src={getAvatarUrl(opponentProfile?.avatar_url)} />
                <AvatarFallback>{opponentProfile?.username?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
            </div>
            
            {/* Usernames below - left and right aligned */}
            <div className="flex items-start justify-between">
              <div className="flex-1 text-left">
                <p className="font-semibold text-blue-500">{currentUserProfile?.username}</p>
                <p className="text-xs text-muted-foreground">{userMetricInfo?.label}</p>
              </div>
              <div className="flex-1 text-right">
                <p className="font-semibold text-red-500">{opponentProfile?.username}</p>
                <p className="text-xs text-muted-foreground">{opponentMetricInfo?.label}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Score */}
        <div className="grid grid-cols-3 gap-4 p-4 bg-background rounded-lg border">
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-500">{scores.userWins}</p>
            <p className="text-xs text-muted-foreground">{t('ui.text.you_won')}</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-slate-500">{scores.ties}</p>
            <p className="text-xs text-muted-foreground">{t('ui.text.ties')}</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-red-500">{scores.opponentWins}</p>
            <p className="text-xs text-muted-foreground">{t('ui.text.they_won')}</p>
          </div>
        </div>

        {/* Winner */}
        {scores.userWins !== scores.opponentWins && (
          <div className="p-4 bg-background rounded-lg border text-center">
            <Trophy className={`w-8 h-8 mx-auto mb-2 ${scores.userWins > scores.opponentWins ? 'text-blue-500' : 'text-red-500'}`} />
            <p className={`text-lg font-bold ${scores.userWins > scores.opponentWins ? 'text-blue-500' : 'text-red-500'}`}>
              {scores.userWins > scores.opponentWins ? currentUserProfile?.username : opponentProfile?.username} Wins!
            </p>
          </div>
        )}

        {/* Graph */}
        <div className="p-4 bg-background rounded-lg border">
          <TooltipProvider>
            <div className="flex gap-1 justify-center">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="flex flex-col gap-1">
                  {week.map((day, dayIndex) => {
                    const userValue = getValueFromEntry(day.userEntry, userMetric);
                    const opponentValue = getValueFromEntry(day.opponentEntry, opponentMetric);

                    let bgColor = emptyColor;
                    let statusText = "No data";

                    if (userValue !== null || opponentValue !== null) {
                      const userPercentage = getPercentage(userValue, userGoal);
                      const opponentPercentage = getPercentage(opponentValue, opponentGoal);

                      if (userPercentage > opponentPercentage) {
                        bgColor = blueColor;
                        statusText = t('ui.text.you_won');
                      } else if (opponentPercentage > userPercentage) {
                        bgColor = redColor;
                        statusText = t('ui.text.they_won');
                      } else {
                        bgColor = tieColor;
                        statusText = t('ui.text.tie');
                      }
                    }

                    return (
                      <Tooltip key={dayIndex}>
                        <TooltipTrigger>
                          <div
                            className="w-3 h-3 rounded-sm transition-opacity hover:opacity-80"
                            style={{ backgroundColor: bgColor }}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs font-medium">{format(day.date, "MMM d, yyyy")}</p>
                          <p className="text-xs">You: {userValue ?? 0}</p>
                          <p className="text-xs">{opponentProfile?.username}: {opponentValue ?? 0}</p>
                          <p className="text-xs font-semibold mt-1">{statusText}</p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              ))}
            </div>
          </TooltipProvider>
          
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 md:gap-6 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: blueColor }} />
              <span className="whitespace-nowrap">{t('ui.text.you_won')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: redColor }} />
              <span className="whitespace-nowrap">{t('ui.text.they_won')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: tieColor }} />
              <span className="whitespace-nowrap">{t('ui.text.tie')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: emptyColor }} />
              <span className="whitespace-nowrap">{t('ui.text.no_data')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* User Search */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t('ui.text.search_for_an_opponent')}</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('forms.placeholders.search_by_username')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Search Results */}
        {searchQuery.length >= 2 && (
          <div className="mt-2 border rounded-lg max-h-64 overflow-y-auto">
            {searching ? (
              <div className="p-4 text-center text-sm text-muted-foreground">{t('ui.text.searching')}</div>
            ) : searchResults.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">{t('ui.text.no_users_found')}</div>
            ) : (
              <div className="divide-y">
                {searchResults.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => {
                      setSelectedOpponentId(user.id);
                      setSearchQuery("");
                    }}
                    className="w-full p-3 flex items-center gap-3 hover:bg-accent transition-colors"
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={getAvatarUrl(user.avatar_url)} />
                      <AvatarFallback>{user.username?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="text-left">
                      <p className="font-medium">{user.username}</p>

                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected Opponent */}
      {opponentProfile && (
        <div className="p-4 bg-background border rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={getAvatarUrl(opponentProfile.avatar_url)} />
              <AvatarFallback>{opponentProfile.username?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">{opponentProfile.username}</p>
              <p className="text-xs text-muted-foreground">{t('ui.text.selected_opponent')}</p>
            </div>
          </div>
          <button
            onClick={() => setSelectedOpponentId("")}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Change
          </button>
        </div>
      )}

      {/* Metric Selectors */}
      {selectedOpponentId && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-blue-500">{t('ui.text.your_metric')}</label>
            <Select value={userMetric} onValueChange={setUserMetric}>
              <SelectTrigger className="border-blue-500">
                <SelectValue placeholder={t('forms.placeholders.select_your_metric')} />
              </SelectTrigger>
              <SelectContent>
                {userAvailableMetrics.map(m => (
                  <SelectItem key={m.key} value={m.key}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-red-500">{t('ui.text.opponent_s_metric')}</label>
            <Select value={opponentMetric} onValueChange={setOpponentMetric}>
              <SelectTrigger className="border-red-500">
                <SelectValue placeholder={t('forms.placeholders.select_opponent_metric')} />
              </SelectTrigger>
              <SelectContent>
                {opponentAvailableMetrics.map(m => (
                  <SelectItem key={m.key} value={m.key}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Comparison Result */}
      {renderComparisonGraph()}

      {!selectedOpponentId && (
        <div className="text-center py-12 text-muted-foreground">
          <p>{t('ui.text.search_and_select_an_opponent_to_start_the_head_to')}</p>
        </div>
      )}
    </div>
  );
};

export default CompareHeadToHead;
